import { getCache, getCacheConfig } from '../cache';
import type { PromiseThunkAction } from '../types';

import apiAction from './apiAction';
import type { CachingStrategyFetch, CachingStrategyRead, CachingStrategyWrite } from './cachingStrategies';
import { getCachingStrategy } from './cachingStrategies';
import type { ManagedApiActionOptions } from './types';

export interface CachedApiActionResult<TResponse> {
  readonly response: TResponse;
  readonly cacheHit: boolean | undefined;
}

const cachedApiAction =
  <TResponse>({
    request,
    caching,
    lifecycle: { fulfilled, deferredFulfill },
    telemetry,
  }: ManagedApiActionOptions<TResponse>): PromiseThunkAction<CachedApiActionResult<TResponse>> =>
  async (dispatch) => {
    const cache = getCache();
    const cachingStrategy = caching && caching.strategy;
    const cacheKey = caching && caching.key;
    let cacheHit: boolean | undefined;

    const read: CachingStrategyRead<TResponse> = async () => {
      if (!cache || !cacheKey) {
        return null;
      }
      const item = await cache.getItem<TResponse>(cacheKey);
      if (item) {
        cacheHit = true;
        const callback = caching?.cacheHitCallback;
        if (callback) {
          callback();
        }
      } else {
        cacheHit = false;
      }

      return item;
    };
    const write: CachingStrategyWrite<TResponse> = async (response) => {
      if (!cache || !cacheKey) {
        return;
      }
      const cacheConfig = getCacheConfig();
      const cachingTtlInSeconds = (caching && caching.ttlInSeconds) ?? (cacheConfig && cacheConfig.ttlInSeconds);
      await cache.setItem(cacheKey, response, cachingTtlInSeconds);

      if (caching.strategy === 'ReadCacheAndRevalidateWithFulfill') {
        dispatch(fulfilled(response));
      }

      if (
        caching.strategy === 'ReadCacheAndRevalidateWithDeferredFulfill' ||
        caching.strategy === 'ReadCacheOrFetchWithTimeoutDeferredFulfill'
      ) {
        if (deferredFulfill) {
          dispatch(deferredFulfill(response));
        }
      }

      const cacheUpdateCallback = caching?.cacheUpdateCallback;
      if (cacheUpdateCallback) {
        cacheUpdateCallback();
      }
    };
    const fetch: CachingStrategyFetch<TResponse> = () => dispatch(apiAction<TResponse>(request, telemetry));

    const strategy = getCachingStrategy(cachingStrategy);

    const result = await strategy<TResponse>(read, write, fetch);

    return { response: result, cacheHit };
  };

export default cachedApiAction;
